'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useI18n } from '~/features/i18n';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { Icon } from '~/components/primitives';
import { cx } from 'class-variance-authority';
import type { SB_SubNavBlockProps, SB_SubNavItemBlockProps, SB_SubNavSpotlightBlockProps } from '~/storyblok/components/types.generated';
import { StoryblokLink } from '~/storyblok/utils/StoryblokLink';
import { StoryblokUtils } from '~/storyblok/utils';
import type { ISbStoryData } from '@storyblok/react';
const IconColorVariant = {
  GREEN: 'text-secondary',
  YELLOW: 'text-yellow-600',
  SALMON: 'text-salmon-500',
  BLUE: 'text-blue-800'
} as const;
type SubMenuDesktopKey = SubMenuItemProps['item']['component'];
type SubMenuItemMobileKey = 'list-col' | 'block' | 'icon-block';
type SubMenuItemProps = {
  item: SB_SubNavSpotlightBlockProps | SB_SubNavBlockProps | SB_SubNavItemBlockProps;
  force?: SubMenuItemMobileKey;
};
export const SubMenuItem: React.FC<SubMenuItemProps> = props => {
  const $ = _c(4);
  const keysMapping = {
    "list-col": "SubNavSpotlightBlock",
    block: "SubNavBlock",
    "icon-block": "SubNavItemBlock"
  };
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      SubNavSpotlightBlock,
      SubNavBlock,
      SubNavItemBlock: SubMenuIconBlock
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const mapping = t0 as const satisfies Record<SubMenuDesktopKey, any>;
  const desktopKey = props.force ? keysMapping[props.force] : undefined;
  const Component = mapping[desktopKey ?? props.item.component];
  let t1;
  if ($[1] !== Component || $[2] !== props.item) {
    t1 = Component && <Component {...props.item as any} />;
    $[1] = Component;
    $[2] = props.item;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const SubNavSpotlightBlock: React.FC<SB_SubNavSpotlightBlockProps | SB_SubNavBlockProps> = props => {
  const $ = _c(11);
  const i18n = useI18n();
  const t0 = props.title ?? "";
  let t1;
  if ($[0] !== i18n || $[1] !== props.linkText) {
    t1 = props.linkText ? props.linkText : i18n._("All events");
    $[0] = i18n;
    $[1] = props.linkText;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = props.link?.cached_url ? `/${props.link.cached_url}` : "/";
  let t3;
  if ($[3] !== i18n || $[4] !== props) {
    t3 = "subNav" in props && props.subNav?.map(item => <StoryblokLink key={item._uid || item.uuid || item.name} type="story" link={item}>{StoryblokUtils.Story.translatedName(item, i18n.locale)}</StoryblokLink>);
    $[3] = i18n;
    $[4] = props;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <SubMenuListColWrapper title={t0} linkText={t1} href={t2}>{t3}</SubMenuListColWrapper>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const SubNavBlock: React.FC<SB_SubNavBlockProps> = props => {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <figure><Icon className="text-blue-400" name="chevron-right" /></figure>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.title) {
    t1 = props.title && <h2 className="text-lg">{props.title}</h2>;
    $[1] = props.title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.text) {
    t2 = props.text && <p className="text-sm text-blue-300">{props.text}</p>;
    $[3] = props.text;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="col-start-2 -col-end-1">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== props.link || $[9] !== t3) {
    t4 = <NavigationMenuPrimitive.Item asChild={true}><StoryblokLink type="link" link={props.link} className="[grid-template-columns:subgrid;] col-start-1 -col-end-1 grid flex-col items-center px-6 py-1">{t0}{t3}</StoryblokLink></NavigationMenuPrimitive.Item>;
    $[8] = props.link;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const SubMenuIconBlock: React.FC<SB_SubNavItemBlockProps> = props => {
  const $ = _c(16);
  const t0 = `/${(props.subNavItem as ISbStoryData | undefined)?.full_slug}`;
  let t1;
  if ($[0] !== props.icon || $[1] !== props.iconColor) {
    t1 = props.icon && <Icon viewBox="0 0 46 46" className={cx("h-auto w-auto", IconColorVariant[props?.iconColor !== "BLUE" ? props?.iconColor || "GREEN" : "GREEN"])} name={props.icon as Icon.Name} />;
    $[0] = props.icon;
    $[1] = props.iconColor;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <figure className="h-10 w-10">{t1}</figure>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== props.title) {
    t3 = props.title && <h2 className="text-lg">{props.title}</h2>;
    $[5] = props.title;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props.text) {
    t4 = props.text && <p className="text-sm text-blue-300">{props.text}</p>;
    $[7] = props.text;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="col-start-2 -col-end-1">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t0 || $[13] !== t2 || $[14] !== t5) {
    t6 = <NavigationMenuPrimitive.Item asChild={true}><Link href={t0} className="[grid-template-columns:subgrid;] col-start-1 -col-end-1 grid flex-col items-center px-6 py-1">{t2}{t5}</Link></NavigationMenuPrimitive.Item>;
    $[12] = t0;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
interface SubMenuListColWrapperProps {
  children: React.ReactNode;
  title: string;
  linkText: string;
  href: string;
}
export function SubMenuListColWrapper(t0) {
  const $ = _c(9);
  const {
    children,
    title,
    linkText,
    href
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <h2 className="text-blue-500">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== href || $[3] !== linkText) {
    t2 = href && linkText && <Link className="text-turqoise-500" href={href}>{linkText}</Link>;
    $[2] = href;
    $[3] = linkText;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t1 || $[7] !== t2) {
    t3 = <NavigationMenuPrimitive.Item className="flex flex-col gap-3 px-24 py-1">{t1}{children}{t2}</NavigationMenuPrimitive.Item>;
    $[5] = children;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}